/**
 * DeweyTreeFilter.tsx - Dewey Classification Flat List
 *
 * Ultra-lightweight implementation with manual virtual scrolling.
 * Only processes and renders visible items.
 * Renders all categories as a single flat list ordered by Dewey code ascending.
 */

import React, { useState, useMemo, useCallback, useRef, startTransition } from 'react';
import { VStack, HStack, Input, Text, Box } from '@chakra-ui/react';
import { Checkbox } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuSearch } from 'react-icons/lu';
import { FilterOption } from '@/types/filter.types';
import { compareCodes } from '@/utilities/deweyCodeParser';

interface DeweyTreeFilterProps {
    options: FilterOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    maxHeight?: number;
}

interface FlatNode {
    code: string;
    label: string;
}

const ITEM_HEIGHT = 32;
const VISIBLE_ITEMS = 10;

export const DeweyTreeFilter: React.FC<DeweyTreeFilterProps> = ({
    options,
    selectedValues,
    onChange,
    maxHeight = 300,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [searchQuery, setSearchQuery] = useState('');
    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    // Flat list of all categories ordered by Dewey code ascending
    const flattenedNodes = useMemo((): FlatNode[] => {
        const query = searchQuery.toLowerCase();

        return [...options]
            .sort((a, b) => compareCodes(a.value, b.value))
            .filter((opt) => {
                if (!query) return true;
                const label = opt.displayLabel || opt.value;
                return label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query);
            })
            .map((opt) => ({
                code: opt.value,
                label: opt.displayLabel || opt.value,
            }));
    }, [options, searchQuery]);

    // Calculate visible items based on scroll
    const { visibleItems, startIndex, totalHeight } = useMemo(() => {
        const total = flattenedNodes.length;
        const totalHeight = total * ITEM_HEIGHT;
        const start = Math.floor(scrollTop / ITEM_HEIGHT);
        const visibleCount = Math.min(VISIBLE_ITEMS + 2, total - start);
        
        return {
            visibleItems: flattenedNodes.slice(start, start + visibleCount),
            startIndex: start,
            totalHeight,
        };
    }, [flattenedNodes, scrollTop]);

    const handleToggle = useCallback((code: string) => {
        const newValues = selectedSet.has(code)
            ? selectedValues.filter((v) => v !== code)
            : [...selectedValues, code];
        onChange(newValues);
    }, [selectedSet, selectedValues, onChange]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        startTransition(() => {
            setScrollTop(e.currentTarget.scrollTop);
        });
    }, []);

    const listHeight = Math.min(flattenedNodes.length * ITEM_HEIGHT, maxHeight);

    return (
        <VStack align="stretch" gap={2}>
            {/* Search */}
            <Box position="relative">
                <Input
                    size="sm"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setScrollTop(0);
                    }}
                    placeholder={t('filter_search') || 'Search...'}
                    paddingLeft={isRTL ? '8px' : '32px'}
                    paddingRight={isRTL ? '32px' : '8px'}
                    dir={isRTL ? 'rtl' : 'ltr'}
                />
                <Box
                    position="absolute"
                    top="50%"
                    left={isRTL ? 'auto' : '8px'}
                    right={isRTL ? '8px' : 'auto'}
                    transform="translateY(-50%)"
                    color="gray.400"
                    pointerEvents="none"
                >
                    <LuSearch size={16} />
                </Box>
            </Box>

            {/* List */}
            {flattenedNodes.length === 0 ? (
                <Text fontSize="sm" color="gray.500" padding={4} textAlign="center">
                    {t('filter_no_categories') || 'No categories available'}
                </Text>
            ) : (
                <Box
                    ref={containerRef}
                    height={`${listHeight}px`}
                    overflowY="auto"
                    onScroll={handleScroll}
                    borderRadius="4px"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                    }}
                >
                    <Box height={`${totalHeight}px`} position="relative">
                        <Box
                            position="absolute"
                            top={`${startIndex * ITEM_HEIGHT}px`}
                            left={0}
                            right={0}
                        >
                            {visibleItems.map((node) => {
                                const isSelected = selectedSet.has(node.code);

                                return (
                                    <Box key={node.code} height={`${ITEM_HEIGHT}px`}>
                                        <HStack
                                            gap={1}
                                            paddingY={1}
                                            paddingX={2}
                                            borderRadius="4px"
                                            _hover={{ background: '#F5F5F5' }}
                                        >
                                            <Checkbox.Root
                                                checked={isSelected}
                                                onCheckedChange={() => handleToggle(node.code)}
                                                flex="1"
                                                cursor="pointer"
                                            >
                                                <HStack gap={2} flex="1" dir={isRTL ? 'rtl' : 'ltr'}>
                                                    <Checkbox.HiddenInput />
                                                    <Checkbox.Control cursor="pointer" />
                                                    <Checkbox.Label cursor="pointer" flex="1">
                                                        <Text fontSize="sm" color="gray.700" dir="auto" lineClamp={1}>
                                                            {node.label}
                                                        </Text>
                                                    </Checkbox.Label>
                                                </HStack>
                                            </Checkbox.Root>
                                        </HStack>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}
        </VStack>
    );
};